/**
 * App del Repartidor (Entregas a Domicilio).
 *
 * Vista móvil ligera para el personal de reparto. Muestra las entregas asignadas,
 * datos del cliente con llamada directa, dirección con enlace a Google Maps,
 * cobro esperado y botones táctiles de "En Ruta" y "✓ Entregado".
 */
import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { alRecibir, desconectar } from '../lib/socket';

interface EntregaDomicilio {
  id: number;
  orden_id: number;
  costo_envio: number | string;
  forma_pago_esperada: 'efectivo' | 'terminal' | 'transferencia';
  estado_entrega: 'pendiente' | 'en_ruta' | 'entregado' | 'cancelado';
  total: number | string;
  notas_orden: string | null;
  creado_en: string;
  cliente_nombre: string;
  cliente_telefono: string;
  calle_numero: string;
  colonia: string | null;
  referencias: string | null;
  codigo_postal: string | null;
}

export function Repartidor({ sucursalId }: { sucursalId: number }) {
  const [entregas, setEntregas] = useState<EntregaDomicilio[]>([]);
  const [cargando, setCargando] = useState(true);
  const [actualizandoId, setActualizandoId] = useState<number | null>(null);

  const cargarEntregas = useCallback(async () => {
    try {
      const res = await api<EntregaDomicilio[]>(`/domicilio/repartidor/pendientes?sucursalId=${sucursalId}`);
      setEntregas(res);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }, [sucursalId]);

  useEffect(() => {
    cargarEntregas();
    const desSub = alRecibir('orden:nueva', () => cargarEntregas());
    return () => {
      desSub();
      desconectar();
    };
  }, [cargarEntregas]);

  async function tomarEntrega(ordenId: number) {
    try {
      setActualizandoId(ordenId);
      await api(`/domicilio/repartidor/asignar?sucursalId=${sucursalId}`, {
        method: 'POST',
        body: { ordenId },
      });
      await cargarEntregas();
    } catch (err: any) {
      alert(err?.message || 'Error al tomar entrega.');
    } finally {
      setActualizandoId(null);
    }
  }

  async function cambiarEstado(ordenId: number, nuevoEstado: 'en_ruta' | 'entregado') {
    try {
      setActualizandoId(ordenId);
      await api(`/domicilio/repartidor/estado?sucursalId=${sucursalId}`, {
        method: 'PATCH',
        body: { ordenId, estadoEntrega: nuevoEstado },
      });
      await cargarEntregas();
    } catch (err: any) {
      alert(err?.message || 'Error al actualizar entrega.');
    } finally {
      setActualizandoId(null);
    }
  }

  if (cargando) {
    return <p className="py-24 text-center text-salvia font-bold">Cargando entregas a domicilio…</p>;
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6 font-cuerpo text-tinta">
      <header className="mb-6 flex items-center justify-between border-b border-salvia/15 pb-4">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-nopal-oscuro">App del Repartidor</span>
          <h1 className="font-titulo text-2xl font-extrabold text-tinta">Mis Entregas ({entregas.length})</h1>
        </div>

        <button
          onClick={() => cargarEntregas()}
          className="rounded-xl border border-salvia/30 bg-cal px-3 py-2 text-xs font-bold text-salvia"
        >
          🔄 Actualizar
        </button>
      </header>

      {entregas.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-salvia/30 bg-cal/30 p-12 text-center text-salvia">
          <span className="text-4xl">🛵</span>
          <p className="font-titulo text-lg font-bold text-tinta mt-3">Sin entregas pendientes</p>
          <p className="text-xs text-salvia mt-1">Los pedidos a domicilio asignados aparecerán aquí.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {entregas.map((ent) => {
            const direccionTexto = `${ent.calle_numero}${ent.colonia ? `, ${ent.colonia}` : ''}`;
            const urlMapa = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              `${direccionTexto}, ${ent.codigo_postal || ''}`
            )}`;
            const urlTel = `tel:${ent.cliente_telefono}`;
            const urlWsp = `https://wa.me/52${ent.cliente_telefono.replace(/\D/g, '')}`;

            return (
              <div
                key={ent.id}
                className="rounded-3xl border border-salvia/20 bg-white p-5 shadow-md transition"
              >
                <div className="flex items-center justify-between border-b border-salvia/15 pb-3 mb-3">
                  <div>
                    <span className="text-xs font-bold text-salvia">Pedido a Domicilio</span>
                    <h3 className="font-titulo text-lg font-bold text-tinta">Orden #{ent.orden_id}</h3>
                  </div>

                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold ${
                      ent.estado_entrega === 'en_ruta'
                        ? 'bg-miel text-white animate-pulse'
                        : 'bg-nopal-claro text-nopal-oscuro'
                    }`}
                  >
                    {ent.estado_entrega === 'en_ruta' ? '🛵 En Ruta' : 'Pendiente'}
                  </span>
                </div>

                {/* Cliente y Teléfono */}
                <div className="mb-3 space-y-1">
                  <span className="font-titulo text-base font-bold text-tinta block">{ent.cliente_nombre}</span>
                  <div className="flex gap-2">
                    <a
                      href={urlTel}
                      className="inline-flex items-center gap-1 rounded-xl bg-cal px-3 py-1.5 text-xs font-bold text-nopal-oscuro hover:bg-nopal-claro"
                    >
                      📞 Llamar ({ent.cliente_telefono})
                    </a>
                    <a
                      href={urlWsp}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-xl bg-nopal-claro px-3 py-1.5 text-xs font-bold text-nopal-oscuro"
                    >
                      💬 WhatsApp
                    </a>
                  </div>
                </div>

                {/* Dirección y Referencias */}
                <div className="mb-4 rounded-2xl border border-salvia/15 bg-cal/40 p-3">
                  <p className="text-sm font-bold text-tinta">{direccionTexto}</p>
                  {ent.referencias && (
                    <p className="text-xs text-salvia mt-0.5">Ref: {ent.referencias}</p>
                  )}
                  <a
                    href={urlMapa}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-xs font-bold text-nopal-oscuro underline"
                  >
                    🗺️ Abrir en Google Maps
                  </a>
                </div>

                {/* Cobro */}
                <div className="flex items-center justify-between border-t border-salvia/15 pt-3 mb-4">
                  <div>
                    <span className="text-xs text-salvia block">Cobro al cliente:</span>
                    <span className="text-xs font-bold capitalize text-tinta">
                      {ent.forma_pago_esperada === 'efectivo' ? '💵 Efectivo' : '💳 Terminal Móvil'}
                    </span>
                  </div>
                  <span className="font-titulo text-2xl font-extrabold text-nopal-oscuro">
                    ${Number(ent.total).toFixed(2)}
                  </span>
                </div>

                {/* Botones de Acción */}
                <div className="space-y-2">
                  {ent.estado_entrega === 'pendiente' ? (
                    <button
                      disabled={actualizandoId === ent.orden_id}
                      onClick={() => tomarEntrega(ent.orden_id)}
                      className="w-full btn-principal py-3 font-bold"
                    >
                      🛵 Iniciar Entrega (En Ruta)
                    </button>
                  ) : (
                    <button
                      disabled={actualizandoId === ent.orden_id}
                      onClick={() => cambiarEstado(ent.orden_id, 'entregado')}
                      className="w-full btn-principal py-3 font-bold bg-nopal hover:bg-nopal-oscuro"
                    >
                      ✓ Marcar Entregado al Cliente
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
